/**
 * A single row of mutually-exclusive filter pills (a `role="tablist"`).
 * Shared by the earnings + payouts surfaces (range filter, status filter)
 * so agent and recruiter don't each grow their own copy — the "extract
 * before the second copy lands" rule. Labels are always passed in already
 * resolved through `useI18n()`; this component renders no literals.
 */
export interface FilterOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export function FilterPills<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
}: {
  readonly ariaLabel: string;
  readonly value: T;
  readonly options: readonly FilterOption<T>[];
  readonly onChange: (next: T) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '0.375em 0.875em',
              minHeight: 'var(--touch-target)',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border)',
              background: selected ? 'var(--color-primary)' : 'var(--color-surface)',
              color: selected ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
              fontSize: 'var(--text-meta)',
              fontWeight: 500,
              lineHeight: 1.3,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background var(--duration-1) var(--ease-snappy)',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
